/**
 * Configuration Context Provider
 *
 * Provides centralised access to all configuration types (service, app, env)
 * across the application. Dynamically discovers and loads all YAML config files
 * at startup based on VITE_APP_ENV, without hardcoding file names.
 *
 * Loading pipeline:
 *   1. Discover all *.yml files under configs/{env}/{serviceConfigs,appConfigs,envConfigs}/
 *   2. Load and deep-merge files within each folder type
 *   3. Resolve ${VARNAME} placeholders from import.meta.env
 *   4. Validate every placeholder variable is present in import.meta.env
 *   5. Extract typed configs and assemble AuthConfig
 *   6. Expose configs via React Context
 */

import React, { createContext, useContext, useEffect, useState } from 'react'
import { AppLogger } from '@/lib/logger'
import { parseLoggerConfig } from '@/lib/logger/loggerConfigParser'
import { initOtel } from '@/lib/logger/otelBootstrap'
import type { LoggerClientConfig } from '@/lib/logger/loggerTypes'

const log = AppLogger.getLogger(import.meta)

// ─── APP ENVIRONMENT ─────────────────────────────────────────────────────────

type AppEnvironment = 'development' | 'staging' | 'production'

const APP_ENVIRONMENTS: AppEnvironment[] = ['development', 'staging', 'production']

/**
 * Resolve and validate the VITE_APP_ENV environment variable.
 *
 * @returns The active AppEnvironment
 * @throws Error if VITE_APP_ENV is missing or not a valid environment name
 */
function resolveAppEnv(): AppEnvironment {
  const rawAppEnv = import.meta.env?.VITE_APP_ENV
  if (typeof rawAppEnv !== 'string' || rawAppEnv.trim() === '') {
    throw new Error('Missing required environment variable: VITE_APP_ENV')
  }

  const normalized = rawAppEnv.trim()
  if (!APP_ENVIRONMENTS.includes(normalized as AppEnvironment)) {
    throw new Error(
      `Invalid VITE_APP_ENV value: ${rawAppEnv}. Expected one of ${APP_ENVIRONMENTS.join(', ')}`
    )
  }

  return normalized as AppEnvironment
}

const VITE_APP_ENV = resolveAppEnv()

// ─── INTERFACES & TYPES ──────────────────────────────────────────────────────
//
// All interface and type definitions are grouped here for easy reference.
// Domain interfaces (consumer-visible) are frozen; changing them would break
// existing callers. Internal types are used only within this module.

// ── Domain interfaces (frozen: consumer-visible, must not change shape) ──────

interface ServiceConfig {
  apiGateway: {
    baseUrl: string
    version: string
    timeout: number
  }
  logging?: {
    sdk?: {
      module?: string
      component?: string
      logLevel?: string
      consoleStructured?: boolean
      commonInfoKeys?: string[]
      otel?: {
        enabled?: boolean | string
        serviceName?: string
        serviceVersion?: string
        environment?: string
        collectorUrl?: string
        headers?: Record<string, string>
        propagators?: string[]
        batch?: {
          scheduledDelayMillis?: number
          maxExportBatchSize?: number
          maxQueueSize?: number
          exportTimeoutMillis?: number
        }
      }
    }
  }
}

type ApiEndpointConfig = {
  path: string
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  version?: string
  // Optional schema overrides for non-standard endpoints.
  // If present, these take precedence over convention-based lookup.
  schemas?: {
    request?: string   // Schema name (e.g., 'LoginRequestSchema' or 'z.any()')
    response?: string  // Schema name (e.g., 'LoginResponseSchema' or 'z.any()')
  }
  // Optional mock data configuration
  // true = use convention, string = custom file name
  mock?: boolean | string
}

interface ApiConfig {
  endpointVersion: string
  endpointConfigs: Record<string, ApiEndpointConfig>
}

interface AuthConfig {
  authentication: {
    azure: {
      clientId: string
      tenantId: string
      redirectUri: string
      scopes: string[]
      cacheLocation: 'sessionStorage' | 'localStorage'
    }
  }
}

interface RouteAccessConfig {
  title?: string
  isSecured: boolean
  accessTo: string[]
}

interface RoutesConfig {
  routes: Record<string, RouteAccessConfig>
  defaults: {
    isSecured: boolean
    accessTo: string[]
  }
}

interface EnvConfig {
  env?: Record<string, unknown>
  [key: string]: string | boolean | Record<string, unknown> | undefined
}

// ── Internal types (not exported; used within this module only) ──────────────

/** The three known sub-folder categories under each environment's config directory */
type FolderType = 'serviceConfigs' | 'appConfigs' | 'envConfigs'

/** Deep-merged result of all YAML files within one folder type */
type FolderContents = Record<string, unknown>

/**
 * Subset of the YAML_MODULES glob map scoped to a single folder type.
 * Keys are module paths; values are Vite lazy-loader functions.
 */
type GroupedModules = Record<string, () => Promise<unknown>>

/**
 * All merged YAML data keyed by folder type.
 * Populated by loadAllConfigsForEnv() and consumed by the extraction layer.
 */
type RawConfigByFolder = {
  [K in FolderType]?: FolderContents
}

// ── Context and provider types ────────────────────────────────────────────────

/** Shape of the value provided by ConfigContext */
interface ConfigContextType {
  /** Typed extract from serviceConfigs/*.yml deep-merge */
  serviceConfig: ServiceConfig | null
  /** Typed extract from appConfigs/*.yml deep-merge (endpoint registry) */
  apiConfig: ApiConfig | null
  /** Typed extract from appConfigs/*.yml deep-merge (route access control) */
  routesConfig: RoutesConfig | null
  /** Assembled from resolved envConfigs env block */
  authConfig: AuthConfig | null
  /** Parsed configuration for the Logger Client SDK */
  loggerConfig: LoggerClientConfig | null
  /** Full resolved envConfigs deep-merge (with ${VAR} substituted) */
  envConfig: EnvConfig | null
  /**
   * Base URL that API callers should prefix onto request paths.
   *
   * Empty string under the Vite dev server, where the proxy in vite.config.ts
   * forwards /api/* to the gateway; otherwise serviceConfig.apiGateway.baseUrl.
   * Resolved here so consumers never inspect build mode themselves.
   */
  apiBaseUrl: string
  /**
   * Raw merged YAML data by folder type. Allows access to config keys not yet
   * promoted to typed properties, without requiring ConfigContextType changes.
   * Example: configs.appConfigs?.featureFlags
   */
  configs: RawConfigByFolder
  isLoading: boolean
  error: Error | null
  appEnv: AppEnvironment
}

interface ConfigProviderProps {
  children: React.ReactNode
}

// ─── MODULE-LEVEL CONSTANTS ───────────────────────────────────────────────────

/**
 * Eagerly-registered YAML module loaders for all config files across all
 * environments. Vite resolves this glob at build time; the pattern covers
 * any *.yml file inside serviceConfigs/, appConfigs/, or envConfigs/ under
 * any environment directory.
 *
 * Keys follow the pattern: ../configs/{env}/{folderType}/{filename}.yml
 */
const YAML_MODULES = import.meta.glob('../configs/*/{serviceConfigs,appConfigs,envConfigs}/*.yml')

// ─── UTILITY FUNCTIONS ────────────────────────────────────────────────────────
/**
 * Recursively substitute ${VARNAME} placeholders in an object tree with
 * values from the provided environment variables map.
 *
 * Handles strings, arrays, and plain objects. Non-string primitives are
 * returned unchanged. Missing variable keys emit a warning and leave the
 * placeholder intact.
 *
 * @param obj     - Any value from a parsed YAML tree
 * @param envVars - Map of variable names to their resolved string values
 * @returns The input structure with all resolvable placeholders replaced
 */
function substituteEnvVars(obj: unknown, envVars: Record<string, unknown>): unknown {
  if (typeof obj === 'string') {
    return obj.replace(/\$\{(\w+)\}/g, (_, key) => {
      const value = envVars[key]
      if (value === undefined) {
        log.warn(`Environment variable not found: ${key}`)
        return `\$\{${key}\}`
      }
      return String(value)
    })
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => substituteEnvVars(item, envVars))
  }

  if (typeof obj === 'object' && obj !== null) {
    const result: Record<string, unknown> = {}
    for (const key in obj as Record<string, unknown>) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = substituteEnvVars((obj as Record<string, unknown>)[key], envVars)
      }
    }
    return result
  }

  return obj
}

/**
 * Walk an object tree and collect every ${VARNAME} placeholder key found.
 *
 * Used before substitution to build the complete list of environment variables
 * that must be present in import.meta.env. This enables dynamic validation
 * without hardcoding variable names — any new ${VAR} added to a YAML config
 * file is automatically included in validation.
 *
 * @param obj - Any value from a parsed (pre-substitution) YAML tree
 * @returns Deduplicated array of placeholder key names (without ${ } wrapping)
 */
function extractPlaceholderKeys(obj: unknown): string[] {
  const found = new Set<string>()

  function walk(value: unknown): void {
    if (typeof value === 'string') {
      const matches = value.matchAll(/\$\{(\w+)\}/g)
      for (const match of matches) {
        found.add(match[1])
      }
      return
    }
    if (Array.isArray(value)) {
      value.forEach(walk)
      return
    }
    if (typeof value === 'object' && value !== null) {
      for (const v of Object.values(value as Record<string, unknown>)) {
        walk(v)
      }
    }
  }

  walk(obj)
  return Array.from(found)
}

// ─── FILE-DISCOVERY LAYER ─────────────────────────────────────────────────────

/**
 * Partition the global YAML_MODULES map into sub-maps keyed by FolderType,
 * filtered to only include files belonging to the specified AppEnvironment.
 *
 * Each key in YAML_MODULES follows the pattern:
 *   ../configs/{env}/{folderType}/{filename}.yml
 *
 * Any number of files may exist within each folder type; all are included.
 *
 * @param env - The active AppEnvironment to filter for
 * @returns Object with one GroupedModules entry per FolderType
 */
function groupModulesByFolder(env: AppEnvironment): Record<FolderType, GroupedModules> {
  const pattern =
    /^\.\.\/configs\/([^/]+)\/(serviceConfigs|appConfigs|envConfigs)\/[^/]+\.yml$/

  const grouped: Record<FolderType, GroupedModules> = {
    serviceConfigs: {},
    appConfigs: {},
    envConfigs: {},
  }

  for (const [modulePath, loader] of Object.entries(YAML_MODULES)) {
    const match = pattern.exec(modulePath)
    if (!match) continue

    const [, envSegment, folderSegment] = match
    if (envSegment !== env) continue

    grouped[folderSegment as FolderType][modulePath] = loader
  }

  return grouped
}

/**
 * Deeply merge two plain objects, with source values overwriting target values.
 *
 * Plain objects are merged recursively. Arrays and all other primitives follow
 * source-wins semantics. The target object is mutated and returned.
 *
 * Used to combine multiple YAML files within the same folder type into one
 * coherent configuration object. If two files define the same top-level key,
 * the file loaded last wins (files are processed in alphabetical path order).
 *
 * @param target - Base object to merge into (mutated in-place)
 * @param source - Object whose values are merged on top of target
 * @returns The mutated target object
 */
function deepMerge(target: FolderContents, source: FolderContents): FolderContents {
  for (const [key, sourceValue] of Object.entries(source)) {
    const targetValue = target[key]
    if (
      typeof sourceValue === 'object' &&
      sourceValue !== null &&
      !Array.isArray(sourceValue) &&
      typeof targetValue === 'object' &&
      targetValue !== null &&
      !Array.isArray(targetValue)
    ) {
      target[key] = deepMerge(targetValue as FolderContents, sourceValue as FolderContents)
    } else {
      target[key] = sourceValue
    }
  }

  return target
}

/**
 * Load all YAML files in a GroupedModules map and deep-merge their contents
 * into a single FolderContents object.
 *
 * Each module may export data as an ES module default ({ default: data })
 * or as the data object directly. Both forms are normalised before merging.
 *
 * @param loaders - Map of module paths to Vite lazy-loader functions
 * @returns Promise resolving to the deep-merged content of all loaded files
 */
async function loadFolderModules(loaders: GroupedModules): Promise<FolderContents> {
  const entries = Object.entries(loaders)

  if (entries.length === 0) {
    return {}
  }

  const loaded = await Promise.all(
    entries.map(async ([, loader]) => {
      const module = await loader()
      const data = (module as { default?: unknown }).default ?? module
      return data as FolderContents
    })
  )

  return loaded.reduce<FolderContents>((merged, current) => deepMerge(merged, current), {})
}

/**
 * Discover, load, and deep-merge all YAML config files for a given
 * AppEnvironment, grouped by folder type.
 *
 * Supports an indeterminate number of YAML files per folder — any file
 * present in serviceConfigs/, appConfigs/, or envConfigs/ is automatically
 * included without requiring code changes.
 *
 * Loads all three folder types in parallel for efficiency.
 *
 * @param env - The active AppEnvironment
 * @returns RawConfigByFolder containing merged FolderContents per FolderType
 * @throws Error if any config file fails to load
 */
async function loadAllConfigsForEnv(env: AppEnvironment): Promise<RawConfigByFolder> {
  const grouped = groupModulesByFolder(env)

  const folderTypes: FolderType[] = ['serviceConfigs', 'appConfigs', 'envConfigs']

  const results = await Promise.all(
    folderTypes.map(async (folderType) => {
      const contents = await loadFolderModules(grouped[folderType])
      return [folderType, contents] as const
    })
  )

  return Object.fromEntries(results) as RawConfigByFolder
}

// ─── EXTRACTION LAYER ─────────────────────────────────────────────────────────

/**
 * Extract and type-assert ServiceConfig from the deep-merged serviceConfigs data.
 *
 * All serviceConfigs/*.yml files have already been merged into a single object
 * before this function is called, so it operates on the combined result
 * regardless of how many source files exist.
 *
 * @param merged - Merged contents of all serviceConfigs/*.yml files
 * @param env    - Active AppEnvironment (used in error messages)
 * @returns Typed ServiceConfig object
 * @throws Error if the required apiGateway key is absent
 */
function extractServiceConfig(merged: FolderContents, env: AppEnvironment): ServiceConfig {
  if (!merged.apiGateway || typeof merged.apiGateway !== 'object') {
    throw new Error(
      `Missing required 'apiGateway' key in serviceConfigs for VITE_APP_ENV=${env}`
    )
  }

  return merged as unknown as ServiceConfig
}

/**
 * Extract and type-assert ApiConfig from the deep-merged appConfigs data.
 *
 * Both apiRoutes.yml and any additional appConfigs files are pre-merged, so
 * this function reads endpointVersion and endpointConfigs from the unified
 * object regardless of how many source files contributed those keys.
 *
 * @param merged - Merged contents of all appConfigs/*.yml files
 * @param env    - Active AppEnvironment (used in error messages)
 * @returns Typed ApiConfig object
 * @throws Error if endpointVersion or endpointConfigs are absent
 */
function extractApiConfig(merged: FolderContents, env: AppEnvironment): ApiConfig {
  if (!merged.endpointVersion || typeof merged.endpointVersion !== 'string') {
    throw new Error(
      `Missing required 'endpointVersion' key in appConfigs for VITE_APP_ENV=${env}`
    )
  }

  if (!merged.endpointConfigs || typeof merged.endpointConfigs !== 'object') {
    throw new Error(
      `Missing required 'endpointConfigs' key in appConfigs for VITE_APP_ENV=${env}`
    )
  }

  return {
    endpointVersion: merged.endpointVersion as string,
    endpointConfigs: merged.endpointConfigs as Record<string, ApiEndpointConfig>,
  }
}

/**
 * Extract and type-assert RoutesConfig from the deep-merged appConfigs data.
 *
 * Reads from the same merged object as extractApiConfig. Both extractors
 * pluck their own top-level keys from the combined appConfigs merge, which
 * means the folder could be split across more files in the future without
 * any changes to this function.
 *
 * @param merged - Merged contents of all appConfigs/*.yml files
 * @param env    - Active AppEnvironment (used in error messages)
 * @returns Typed RoutesConfig object
 * @throws Error if routes or defaults keys are absent
 */
function extractRoutesConfig(merged: FolderContents, env: AppEnvironment): RoutesConfig {
  if (!merged.routes || typeof merged.routes !== 'object') {
    throw new Error(`Missing required 'routes' key in appConfigs for VITE_APP_ENV=${env}`)
  }

  if (!merged.defaults || typeof merged.defaults !== 'object') {
    throw new Error(`Missing required 'defaults' key in appConfigs for VITE_APP_ENV=${env}`)
  }

  return {
    routes: merged.routes as Record<string, RouteAccessConfig>,
    defaults: merged.defaults as { isSecured: boolean; accessTo: string[] },
  }
}

// ─── ENV-VAR RESOLUTION LAYER ─────────────────────────────────────────────────

/**
 * Apply Vite build-time environment variables to the raw envConfigs merged
 * data, replacing all ${VARNAME} placeholders with their resolved values.
 *
 * Two-pass substitution:
 *   Pass 1 — substitute from import.meta.env (Vite build-time variables)
 *   Pass 2 — substitute within the resolved env sub-object against itself,
 *             allowing cross-references (e.g. VITE_SCOPE_ID referenced inside
 *             the VITE_AUTH_SCOPES array value)
 *
 * @param rawEnvContents - Deep-merged contents of all envConfigs/*.yml files
 * @returns EnvConfig with all ${...} placeholders resolved
 * @throws Error if the required env sub-object is missing or malformed
 */
function resolveEnvConfig(rawEnvContents: FolderContents): EnvConfig {
  const viteBuildEnv = import.meta.env as unknown as Record<string, unknown>

  // Pass 1: substitute all ${VARNAME} using Vite build-time env vars
  const afterPass1 = substituteEnvVars(rawEnvContents, viteBuildEnv) as EnvConfig

  if (!afterPass1.env || typeof afterPass1.env !== 'object') {
    throw new Error(
      `Missing required 'env' configuration object in envConfigs for VITE_APP_ENV=${VITE_APP_ENV}`
    )
  }

  // Pass 2: substitute within the env sub-object itself (cross-references)
  const envVars = afterPass1.env as Record<string, unknown>
  const resolvedEnvVars = substituteEnvVars(envVars, envVars) as Record<string, unknown>
  afterPass1.env = resolvedEnvVars

  return afterPass1
}

/**
 * Dynamically validate that every ${VARNAME} placeholder found in the raw
 * envConfigs YAML tree has a resolved, non-empty value in the provided EnvConfig.
 *
 * This function replaces all hardcoded per-variable checks. Any new ${VAR}
 * added to any environment.yml (or future envConfigs file) is automatically
 * included in validation without modifying this function.
 *
 * All missing or unresolved variables are collected and reported in a single
 * error, rather than failing on the first missing variable.
 *
 * @param rawEnvContents - Pre-substitution envConfigs merged data (used to discover keys)
 * @param resolvedEnv    - Post-substitution EnvConfig (used to check resolved values)
 * @param env            - Active AppEnvironment (used in error message)
 * @throws Error listing all missing or unresolved environment variable names
 */
function validateEnvVars(
  rawEnvContents: FolderContents,
  resolvedEnv: EnvConfig,
  env: AppEnvironment
): void {
  const placeholderKeys = extractPlaceholderKeys(rawEnvContents)
  const resolvedEnvVars = (resolvedEnv.env ?? {}) as Record<string, unknown>
  const missingVars: string[] = []

  for (const key of placeholderKeys) {
    const value = resolvedEnvVars[key]
    const isMissing = value === undefined || value === null || value === ''
    const isUnresolved = typeof value === 'string' && /\$\{\w+\}/.test(value)

    if (isMissing || isUnresolved) {
      missingVars.push(key)
    }
  }

  if (missingVars.length > 0) {
    throw new Error(
      `Missing or unresolved environment variables for VITE_APP_ENV=${env}: ` +
        missingVars.join(', ')
    )
  }
}

/**
 * Assemble an AuthConfig from the fully resolved EnvConfig.
 *
 * Reads VITE_CLIENT_ID, VITE_TENANT_ID, VITE_REDIRECT_URI, VITE_AUTH_SCOPES,
 * and VITE_AUTH_CACHE_LOCATION from resolvedEnv.env.
 *
 * Presence of all variables is guaranteed by validateEnvVars() called before
 * this function. This function performs semantic validations that go beyond
 * mere presence: scopes must be a non-empty array, and cacheLocation must be
 * one of the two allowed union values ('sessionStorage' | 'localStorage').
 *
 * @param resolvedEnv - Fully substituted EnvConfig
 * @param env         - Active AppEnvironment (used in error messages)
 * @returns Typed AuthConfig object
 * @throws Error if any required auth field fails semantic type validation
 */
function buildAuthConfig(resolvedEnv: EnvConfig, env: AppEnvironment): AuthConfig {
  const envVars = (resolvedEnv.env ?? {}) as Record<string, unknown>

  const clientId = envVars.VITE_CLIENT_ID
  const tenantId = envVars.VITE_TENANT_ID
  const redirectUri = envVars.VITE_REDIRECT_URI
  const authScopes = envVars.VITE_AUTH_SCOPES
  const authCacheLocation = envVars.VITE_AUTH_CACHE_LOCATION

  if (typeof clientId !== 'string' || clientId.trim() === '') {
    throw new Error(`Missing required envConfig.env.VITE_CLIENT_ID for VITE_APP_ENV=${env}`)
  }
  if (typeof tenantId !== 'string' || tenantId.trim() === '') {
    throw new Error(`Missing required envConfig.env.VITE_TENANT_ID for VITE_APP_ENV=${env}`)
  }
  if (typeof redirectUri !== 'string' || redirectUri.trim() === '') {
    throw new Error(`Missing required envConfig.env.VITE_REDIRECT_URI for VITE_APP_ENV=${env}`)
  }
  if (!Array.isArray(authScopes) || authScopes.length === 0) {
    throw new Error(`Missing required envConfig.env.VITE_AUTH_SCOPES for VITE_APP_ENV=${env}`)
  }
  if (authCacheLocation !== 'sessionStorage' && authCacheLocation !== 'localStorage') {
    throw new Error(
      `Invalid envConfig.env.VITE_AUTH_CACHE_LOCATION for VITE_APP_ENV=${env}. ` +
        `Expected 'sessionStorage' or 'localStorage'`
    )
  }

  return {
    authentication: {
      azure: {
        clientId,
        tenantId,
        redirectUri,
        scopes: authScopes.map((scope) => String(scope)),
        cacheLocation: authCacheLocation,
      },
    },
  }
}

// ─── TOP-LEVEL ORCHESTRATOR ───────────────────────────────────────────────────

/**
 * Resolve the base URL that API callers prefix onto request paths.
 *
 * Under the Vite dev server the proxy declared in vite.config.ts forwards
 * /api/* to the gateway, so requests must stay same-origin and the base URL
 * is empty. Every built artifact — including the deployed development Static
 * Web App, which has no proxy — must call the gateway absolutely.
 *
 * Build mode is read here rather than in consumers so that import.meta.env
 * stays confined to this module.
 *
 * @param serviceConfig - Extracted serviceConfigs data for the active environment
 * @param env           - The active AppEnvironment (used in the error message)
 * @returns Empty string under the dev server, otherwise the gateway base URL
 * @throws Error if a built artifact has no apiGateway.baseUrl configured
 */
function resolveApiBaseUrl(serviceConfig: ServiceConfig, env: AppEnvironment): string {
  if (import.meta.env?.DEV) {
    return ''
  }

  const baseUrl = serviceConfig.apiGateway?.baseUrl
  if (!baseUrl) {
    throw new Error(
      `Missing required 'apiGateway.baseUrl' in serviceConfigs for VITE_APP_ENV=${env}`
    )
  }
  return baseUrl
}

/**
 * Master configuration loader.
 *
 * Orchestrates the full config loading pipeline in strict order:
 *   1. loadAllConfigsForEnv  — discover and load all YAML files by folder type
 *   2. resolveEnvConfig      — substitute ${VARNAME} placeholders from import.meta.env
 *   3. validateEnvVars       — throw if any placeholder variable is missing or unresolved
 *   4. extractServiceConfig  — type-assert from serviceConfigs merged data
 *   5. extractApiConfig      — type-assert from appConfigs merged data
 *   6. extractRoutesConfig   — type-assert from appConfigs merged data (same merge)
 *   7. buildAuthConfig       — assemble from resolved env vars with semantic validation
 *
 * @param env - The active AppEnvironment
 * @returns Object containing all resolved typed configs plus the raw RawConfigByFolder
 * @throws Error (propagated from any sub-function) on any loading or validation failure
 */
async function loadAllConfigurations(env: AppEnvironment): Promise<{
  serviceConfig: ServiceConfig
  apiConfig: ApiConfig
  routesConfig: RoutesConfig
  authConfig: AuthConfig
  envConfig: EnvConfig
  loggerConfig: LoggerClientConfig
  configs: RawConfigByFolder
  apiBaseUrl: string
}> {
  const rawConfigs = await loadAllConfigsForEnv(env)

  const serviceConfigsMerged = rawConfigs.serviceConfigs ?? {}
  const appConfigsMerged = rawConfigs.appConfigs ?? {}
  const envConfigsMerged = rawConfigs.envConfigs ?? {}

  const resolvedEnv = resolveEnvConfig(envConfigsMerged)
  validateEnvVars(envConfigsMerged, resolvedEnv, env)

  const serviceConfig = extractServiceConfig(serviceConfigsMerged, env)
  const apiConfig = extractApiConfig(appConfigsMerged, env)
  const routesConfig = extractRoutesConfig(appConfigsMerged, env)
  const authConfig = buildAuthConfig(resolvedEnv, env)
  const apiBaseUrl = resolveApiBaseUrl(serviceConfig, env)

  const runtimeEnv = (import.meta.env ?? {}) as Record<string, string>
  const loggerConfig = parseLoggerConfig(serviceConfig.logging?.sdk, runtimeEnv)

  log.info(`Configurations loaded: appEnv=${env}`, { appEnv: env })

  return {
    serviceConfig,
    apiConfig,
    routesConfig,
    authConfig,
    loggerConfig,
    envConfig: resolvedEnv,
    configs: rawConfigs,
    apiBaseUrl,
  }
}

// ─── REACT CONTEXT ────────────────────────────────────────────────────────────

const ConfigContext = createContext<ConfigContextType | undefined>(undefined)

/**
 * ConfigProvider
 *
 * Root-level provider that loads all environment-specific YAML configurations
 * at application startup. Renders children only after configuration is
 * available (or in error state). Must wrap all components that call useConfig().
 *
 * @param props.children - Child components that require config access
 */
export function ConfigProvider({ children }: ConfigProviderProps) {
  const [serviceConfig, setServiceConfig] = useState<ServiceConfig | null>(null)
  const [apiConfig, setApiConfig] = useState<ApiConfig | null>(null)
  const [routesConfig, setRoutesConfig] = useState<RoutesConfig | null>(null)
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null)
  const [loggerConfig, setLoggerConfig] = useState<LoggerClientConfig | null>(null)
  const [envConfig, setEnvConfig] = useState<EnvConfig | null>(null)
  const [configs, setConfigs] = useState<RawConfigByFolder>({})
  const [apiBaseUrl, setApiBaseUrl] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    /**
     * Invoke the top-level orchestrator and push results into React state.
     * Runs once on mount; VITE_APP_ENV is module-level and never changes.
     */
    async function initConfigurations(): Promise<void> {
      try {
        setIsLoading(true)

        const result = await loadAllConfigurations(VITE_APP_ENV)

        setServiceConfig(result.serviceConfig)
        setApiConfig(result.apiConfig)
        setRoutesConfig(result.routesConfig)
        setAuthConfig(result.authConfig)
        setLoggerConfig(result.loggerConfig)
        setEnvConfig(result.envConfig)
        setConfigs(result.configs)
        setApiBaseUrl(result.apiBaseUrl)
        AppLogger.configure(result.loggerConfig)

        // Initialize OpenTelemetry if a config block dictates it
        if (result.loggerConfig.otel) {
          initOtel(result.loggerConfig.otel)
        }

        // Expose route access config for the router-level security verifier.
        if (typeof window !== 'undefined') {
          ;(
            window as Window & { __conexRouteAccessConfig?: RoutesConfig }
          ).__conexRouteAccessConfig = result.routesConfig
        }
      } catch (err) {
        const loadError = err instanceof Error ? err : new Error('Failed to load configurations')
        setError(loadError)
        log.error('Configuration loading failed:', loadError)
      } finally {
        setIsLoading(false)
      }
    }

    initConfigurations()
  }, [])

  const value: ConfigContextType = {
    serviceConfig,
    apiConfig,
    routesConfig,
    authConfig,
    loggerConfig,
    envConfig,
    configs,
    apiBaseUrl,
    isLoading,
    error,
    appEnv: VITE_APP_ENV,
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-lg font-semibold">Loading configuration...</div>
          <div className="text-sm text-muted-foreground">Initializing environment</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-lg font-semibold text-destructive">Configuration Error</div>
          <div className="text-sm text-muted-foreground">{error.message}</div>
        </div>
      </div>
    )
  }

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
}

/**
 * useConfig
 *
 * React hook that returns the current ConfigContextType value.
 * Must be called within a component tree wrapped by ConfigProvider.
 *
 * @returns ConfigContextType - the current configuration context value
 * @throws Error if called outside of a ConfigProvider
 */
export function useConfig(): ConfigContextType {
  const context = useContext(ConfigContext)
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider')
  }
  return context
}

// ─── EXPORTS ──────────────────────────────────────────────────────────────────

export type {
  ConfigContextType,
  ServiceConfig,
  ApiConfig,
  RoutesConfig,
  AuthConfig,
  EnvConfig,
  RawConfigByFolder,
  FolderType,
}
